import * as React from "react";
import styled from "styled-components";
import Collapsible from "react-collapsible";
import { Link } from "../../shared/Link";
import AddIcon from "../../../assets/images/add_icon.svg";
import MinusIcon from "../../../assets/minus-icon.svg";
import ArrowIcon from "../../../assets/arrow_right.svg";
import { toREM, toVWMobile } from "../../../helpers/styles";
import * as CommonHeaderTypes from "../types";

export type DrawerMenuItemProps = {
  onClickViewMore: (subMenuItem: CommonHeaderTypes.SubMenuItem) => void;
  menuItem: CommonHeaderTypes.MenuItem;
};

const DrawerMenuItem = ({ onClickViewMore, menuItem }: DrawerMenuItemProps) => {
  if (!menuItem) return;

  const hasSubMenuItems =
    menuItem.subMenuItems && menuItem.subMenuItems?.length > 0;

  const hasOptions = (item: any) => {
    return item?.options?.length > 0;
  };

  return (
    <Collapsible
      easing={"ease-in"}
      transitionTime={300}
      triggerDisabled={!hasSubMenuItems}
      trigger={
        <MainMenuItem to={menuItem.link || undefined}>
          <MainMenuItemText>{menuItem.label}</MainMenuItemText>
          {hasSubMenuItems && (
            <MainMenuItemAction>
              <MainMenuIcon src={AddIcon} alt={"Expand"} />
            </MainMenuItemAction>
          )}
        </MainMenuItem>
      }
      triggerWhenOpen={
        <MainMenuItem to={menuItem.link || undefined}>
          <MainMenuItemText>{menuItem.label}</MainMenuItemText>
          {hasSubMenuItems && (
            <MainMenuItemAction>
              <MainMenuIcon src={MinusIcon} alt={"Collapse"} />
            </MainMenuItemAction>
          )}
        </MainMenuItem>
      }
    >
      {hasSubMenuItems &&
        menuItem.subMenuItems.map(
          (sMenuItem: CommonHeaderTypes.SubMenuItem, index: number) => {
            if (!sMenuItem) return null;

            return (
              <React.Fragment
                key={sMenuItem.link ?? sMenuItem.label ?? `sub-${index}`}
              >
                <SecondaryMenuItem
                  to={
                    hasOptions(sMenuItem)
                      ? undefined
                      : sMenuItem.link || undefined
                  }
                  onClick={() => onClickViewMore(sMenuItem)}
                >
                  <SecondaryMenuItemText>
                    {sMenuItem.label}
                  </SecondaryMenuItemText>
                  <MainMenuItemAction>
                    <SecondaryMenuIcon src={ArrowIcon} alt={"Go"} />
                  </MainMenuItemAction>
                  </SecondaryMenuItem>
                </React.Fragment>
            );
          },
        )}
    </Collapsible>
  );
};

const MainMenuItem = styled(Link)`
  width: 100%;
  height: ${toVWMobile(70)}vw;
  box-sizing: border-box;
  padding: ${toVWMobile(20)}vw ${toVWMobile(30)}vw;
  display: flex;
  justify-content: space-between;
  align-items: center;
  text-decoration: none;
  color: #012604;
  background-color: #f6f1e7;
  border-bottom: 1px solid #012604;
  -webkit-tap-highlight-color: transparent;
`;

const MainMenuItemText = styled.div`
  font-family: "GT Alpina";
  font-size: ${toREM(32)}rem;
  font-weight: 300;
  line-height: 0.9;
  letter-spacing: ${toREM(-1.6)}rem;
  text-transform: none;
  width: ${toVWMobile(280)}vw;
  position: relative;
  z-index: 1;
`;

const MainMenuItemAction = styled.div`
  width: ${toVWMobile(30)}vw;
  height: ${toVWMobile(30)}vw;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 1;
`;

const MainMenuIcon = styled.img`
  width: ${toVWMobile(30)}vw;
  height: ${toVWMobile(30)}vw;
  object-fit: contain;
`;

const SecondaryMenuItem = styled(Link)`
  width: 100%;
  height: ${toVWMobile(60)}vw;
  box-sizing: border-box;
  position: relative;
  z-index: 31;
  padding: ${toVWMobile(20)}vw ${toVWMobile(30)}vw;
  display: flex;
  justify-content: space-between;
  align-items: center;
  text-transform: none;
  -webkit-tap-highlight-color: transparent;
  text-decoration: none;
  color: #012604;
  background-color: #e5e3d7;
  border-bottom: 1px solid #012604;

  &::before,
  &::after {
    content: "";
    position: absolute;
    top: 0;
    bottom: 0;
    width: 2px;
    background-color: #e5e3d7;
    pointer-events: none;
    z-index: 32;
  }

  &::before {
    left: calc(${toVWMobile(15.75)}vw - 0.75px);
  }

  &::after {
    right: calc(${toVWMobile(15.75)}vw - 0.75px);
  }
`;

const SecondaryMenuItemText = styled.div`
  font-family: "GT Alpina";
  font-size: ${toREM(16)}rem;
  font-weight: 300;
  line-height: 0.9;
  letter-spacing: ${toREM(-0.8)}rem;
  width: ${toVWMobile(280)}vw;
  position: relative;
  z-index: 33;
`;

const SecondaryMenuIcon = styled.img`
  object-fit: contain;
  width: ${toVWMobile(30)}vw;
  height: ${toVWMobile(30)}vw;
  position: relative;
  z-index: 33;
  transform: ${(props) =>
    props.theme.isRTL ? `rotate(180deg)` : `rotate(0deg)`};
`;

export default DrawerMenuItem;
